import { apiJson } from "./api/_http";
import { ApiClientError } from "./api/_errors";
import { mapTier } from "./types";
import type { BankrollTier, FamDetail, FamSummary } from "./types";

// Typed client that wraps fetch against the web-app's own Pages Functions
// (which in turn proxy to the services-cf Workers). All routes are
// same-origin so the `__Host-session` HttpOnly cookie is sent automatically;
// the shared apiJson() helper in lib/api/_http.ts handles content-type +
// dev-mode header forwarding.

export { ApiClientError };

// Raw wire shapes — same fields as FamSummary/FamDetail except `tier` is the
// untyped Go enum string (free | fantasy_free | fantasy_premium | hedge). The
// helpers below run mapTier() to collapse it into the pre-seed "free"|"paid"
// axis the UI renders.
type RawFamSummary = Omit<FamSummary, "tier"> & { tier: string };
type RawFamDetail = Omit<FamDetail, "tier"> & { tier: string };

// The /api/me/fams Pages Function passes through the upstream payload
// verbatim, which is `{ fams: [...] }`. We unwrap it here so callsites get a
// plain array.
interface RawListMyFams {
	fams: RawFamSummary[];
}

export async function listMyFams(): Promise<FamSummary[]> {
	const raw = await apiJson<RawListMyFams | RawFamSummary[]>("/api/me/fams");
	const list = Array.isArray(raw) ? raw : raw.fams;
	return list.map((f) => ({ ...f, tier: mapTier(f.tier) }));
}

// Request body for POST /api/me/fams. The FAM Worker accepts more fields
// (composite, allowedMarketCapTiers, voteWindowHours) but the dashboard
// create modal only collects the three below; the rest pick up server
// defaults. isPersonal=true marks the user's single private "Personal"
// Fam (one per account, DB-enforced). isPersonal=false is the Public Fam.
export interface CreateMyFamInput {
	name: string;
	isPersonal: boolean;
	visibility: "public" | "private";
	// Bankroll tier (migration 043). Omitted → server default '10k'.
	// bankrollUsd is required (and only used) when bankrollTier is 'custom'.
	bankrollTier?: BankrollTier;
	bankrollUsd?: number;
}

// FAM Worker returns { fam, member } on success. We only need the slug for
// post-create navigation; the dashboard refetches /api/me/fams to render
// the new card, which carries role + memberCount enriched server-side.
interface RawCreateFamResponse {
	fam: { id: string; slug: string | null; name: string };
}

export async function createMyFam(
	input: CreateMyFamInput,
): Promise<{ id: string; slug: string | null; name: string }> {
	const raw = await apiJson<RawCreateFamResponse>("/api/me/fams", {
		method: "POST",
		body: JSON.stringify(input),
	});
	return raw.fam;
}

export async function getFamBySlug(slug: string): Promise<FamDetail> {
	const raw = await apiJson<RawFamDetail>(`/api/fams/${encodeURIComponent(slug)}`);
	return { ...raw, tier: mapTier(raw.tier) };
}

export async function requestEmailMagicLink(email: string): Promise<void> {
	await apiJson<void>("/api/auth/email/start", {
		method: "POST",
		body: JSON.stringify({ email }),
	});
}

export async function startXOAuth(): Promise<{ authUrl: string }> {
	return apiJson<{ authUrl: string }>("/api/auth/x/start", {
		method: "POST",
		body: JSON.stringify({}),
	});
}

export async function consolidateEmail(
	linked_email: string,
): Promise<{ user_id: string }> {
	return apiJson<{ user_id: string }>("/api/auth/consolidate", {
		method: "POST",
		body: JSON.stringify({ linked_email }),
	});
}
